import { datadogRum } from "@datadog/browser-rum";
import RouterWrapperService from "./UnifyMFEReactRouterWrapper";
import { initializeAnchor } from "./UnifyMFEConfigService";
import { MFE_RUNTIME_STATUS, setMFERuntimeStatus } from "./MFERuntimeStatus";
import { getCookie } from "../../serviceWorker";
import {
  MFE_CONVERGED_ONPREM_CONFIG_URL,
  MFE_UNIFIED_CONFIG_URL,
  VERSION_THRESHOLDS,
} from "../../utils/enums";
import { parseVersion } from "../../utils/common";

const isOnPremVersionSupported = () =>
  parseVersion(window._convergence_version) >=
  VERSION_THRESHOLDS.MIN_ONPREM_MFE;

// remove this while removing Time filter from vAnalytics for MFE integrated pages
// this is to prevent the Time filter from being displayed two times depending on deployment time
window._hide_mfe_time_filter = false;
window._hide_mfe_app360_time_filter = false;

const replacePluginPaths = (config, prefix) =>
  JSON.parse(JSON.stringify(config).replaceAll("{{PluginPath}}", prefix));

const fetchConfigOrThrow = async (configUrl) => {
  const response = await fetch(configUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to load config from ${configUrl}: ${response.status} ${response.statusText}`
    );
  }
  return response.json();
};

export const getCachedMFEConfig = () => {
  const value = localStorage.getItem("mfe_unified_config");
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error("Error parsing value from localStorage:", error);
    localStorage.removeItem("mfe_unified_config");
    return null;
  }
};

export const getDecksConfiguration = async () => {
  try {
    const configUrl = "/analytics/v4/harbor-decks/ui-config.json";
    const config = await fetchConfigOrThrow(configUrl);
    const decksPathPrefix = "/analytics/v4/harbor-decks";
    return replacePluginPaths(config, decksPathPrefix);
  } catch (err) {
    console.error("Failed to load Decks configuration:", err);
    throw err;
  }
};

export const getMFEConfigurations = async () => {
  try {
    const isUIConverged = getCookie("cl-analytics");
    // parseVersion returns 0 for missing, "NA", or malformed values. In a
    // converged session, zero means version discovery was unavailable; it must
    // not be treated as a known pre-26.2 Manager.
    const version = window._convergence_version
      ? parseVersion(window._convergence_version)
      : 0;
    // Known 26.2+ Managers and unknown versions both try the manifest packaged
    // with the Manager image. A known older version continues using the cloud
    // manifest directly because it cannot provide the on-prem MFE artifacts.
    const isOnPremCall =
      isUIConverged && (isOnPremVersionSupported() || !version);
    const configURL = isOnPremCall
      ? MFE_CONVERGED_ONPREM_CONFIG_URL
      : MFE_UNIFIED_CONFIG_URL;
    let pluginPathPrefix = isOnPremCall ? "/assets/mfe" : "";
    let data;
    try {
      data = await fetchConfigOrThrow(configURL);
    } catch (error) {
      // A cloud-manifest failure is terminal because retrying the same source
      // would not provide an alternate recovery path.
      if (configURL === MFE_UNIFIED_CONFIG_URL) throw error;
      // The /assets/mfe prefix is valid only for Manager-hosted plugins. Cloud
      // manifest entries must retain their own hosted paths during fallback.
      pluginPathPrefix = "";
      data = await fetchConfigOrThrow(MFE_UNIFIED_CONFIG_URL);
    }
    return Object.entries(replacePluginPaths(data, pluginPathPrefix)).map(
      ([key, value]) => ({ [key]: value })
    );
  } catch (error) {
    console.error("Failed to load MFE configurations:", error);
    throw error;
  }
};

export const loadMFEConfig = async (forceUpdate = true) => {
  if (window._isAnchorLoaded === true) return;
  setMFERuntimeStatus(MFE_RUNTIME_STATUS.LOADING);
  let unifiedConfig;
  try {
    const cached = getCachedMFEConfig();
    unifiedConfig =
      Array.isArray(cached) && !forceUpdate ? [...cached] : undefined;

    if (!unifiedConfig) {
      if (forceUpdate) localStorage.removeItem("mfe_unified_config");
      const [mfeConfigurations, decksConfig] = await Promise.all([
        getMFEConfigurations(),
        getDecksConfiguration(),
      ]);
      localStorage.setItem(
        "mfe_unified_config",
        JSON.stringify(mfeConfigurations)
      );
      unifiedConfig = mfeConfigurations;
      unifiedConfig.push(decksConfig);
    } else {
      const decksConfig = await getDecksConfiguration();
      unifiedConfig.push(decksConfig);
    }
  } catch (error) {
    console.error("Error loading manifest:", error);
    window._isAnchorLoaded = false;
    setMFERuntimeStatus(MFE_RUNTIME_STATUS.FAILED);
    return;
  }
  window["vanalytics-service"] = window["vanalytics-service"] || {};
  window["vanalytics-service"]["cisco.sdwan.core.shell.nav"] =
    RouterWrapperService;
  window["vanalytics-service"]["cisco.sdwan.core.datadogrum"] = datadogRum;
  try {
    await initializeAnchor(unifiedConfig);
  } catch (error) {
    // initializeAnchor publishes the failed runtime state consumed by MFE renderers.
    console.error("Error initializing MFE runtime:", error);
  }
};
